import React, {
  useEffect, useRef, useState,
} from "react";
import {
  Button, DatePicker, Select,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

import { getUserList } from "../../../actions/aiAnalytics";
import useAbortController from "../../Common/hooks/useAbortController";
import { getUser } from "../../../utils/helpers";
import "./AIAnalyticsFilters.css";

const { RangePicker } = DatePicker;
const defaultUserOption = { value: "", label: "All Users" };

/** Renders date/user filters and the New Prompt action. @param {boolean} disableNewSessionButton - disables New Prompt when composer is active; @param {Function} onNewPrompt - starts a new session; @param {Function} onFiltersChange - emits filter changes to the page */
export default function AIAnalyticsFilters({
  disableNewSessionButton = false,
  onNewPrompt,
  onFiltersChange,
  isUserOptionsLoading,
  setIsUserOptionsLoading,
}) {
  const isMounted = useRef(true);
  const user = getUser();
  const hasUserFilterPermission = user.permissionMap.view_ai_analytics_user_filter;
  const { setNewAbortController, abortController } = useAbortController();
  const [userOptionsList, setUserOptionsList] = useState([defaultUserOption]);
  const [selectedUser, setSelectedUser] = useState(() => (
    hasUserFilterPermission ? "" : (user?.id || "")
  ));
  const [dateRange, setDateRange] = useState(null);
  const hasSyncedFiltersAfterUserLoad = useRef(false);

  useEffect(() => {
    if (!hasUserFilterPermission) {
      setIsUserOptionsLoading(false);
      return undefined;
    }

    if (abortController.current) {
      abortController.current.abort();
    }
    isMounted.current = true;
    const controller = setNewAbortController();

    /** Loads user dropdown options for the Created By filter. */
    const fetchUserOptions = async () => {
      setIsUserOptionsLoading(true);
      try {
        const response = await getUserList({}, controller.signal);
        if (!isMounted.current || response.canceled) return;
        if (response && response.success) {
          const result = [
            defaultUserOption,
            ...(response.results ?? []).map((item) => ({
              value: item.id,
              label: `${item.first_name} ${item.last_name}`.trim(),
            })),
          ];
          setUserOptionsList(result);
          if (user) {
            setSelectedUser(user.id);
          }
        } else {
          toast.error(response?.message);
        }
      } finally {
        setIsUserOptionsLoading(false);
      }
    };

    fetchUserOptions();

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isUserOptionsLoading) return;

    const [startDate, endDate] = dateRange || [];
    if (onFiltersChange) {
      const shouldForce = !hasSyncedFiltersAfterUserLoad.current;
      hasSyncedFiltersAfterUserLoad.current = true;
      onFiltersChange({
        user_filter: selectedUser || null,
        start_date: startDate ? startDate.format("YYYY-MM-DD") : null,
        end_date: endDate ? endDate.format("YYYY-MM-DD") : null,
      }, { force: shouldForce });
    }
  }, [isUserOptionsLoading, selectedUser, dateRange, onFiltersChange]);

  return (
    <div className="AIAnalyticsFilterBar">
      <div className="AIAnalyticsFilterBar__Controls">
        <div className="AIAnalyticsFilterField">
          <label>Date</label>
          <RangePicker
            className="AIAnalyticsFilterDate"
            format="MM/DD/YYYY"
            value={dateRange}
            onChange={(value) => setDateRange(value)}
          />
        </div>
        {hasUserFilterPermission && (
          <div className="AIAnalyticsFilterField">
            <label>Created By</label>
            <Select
              allowClear
              showSearch
              loading={isUserOptionsLoading}
              placeholder="Select user"
              className="AIAnalyticsFilterSelect"
              popupClassName="AIAnalyticsFilterSelect__Menu"
              options={userOptionsList}
              value={selectedUser}
              onSelect={(v) => setSelectedUser(v)}
              onClear={() => setSelectedUser("")}
              filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
              listHeight={400}
            />
          </div>
        )}
      </div>
      <Button
        type="primary"
        className="AIAnalyticsNewPromptButton"
        disabled={disableNewSessionButton}
        onClick={onNewPrompt}
      >
        <PlusOutlined />
        New Prompt
      </Button>
    </div>
  );
}
